import { useQuery } from "@tanstack/react-query";
import {
  NotificationFill30Icon,
  NotificationOutline30Icon,
} from "@vygruppen/spor-icon-react";
import { getBackendUrl } from "api/common";
import { queryFnGET } from "api/tanStackQuery/helpers";
import { TrainInfoState } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainInformationAggregated";
import {
  handleTrainResumedResponse,
  TrainResumed,
  TrainResumedResponse,
} from "features/Header/TrainResumed/useTrainResumed";
import { useCallback, useEffect, useState } from "react";
import { AlertsCounter } from "shared/components/dataDisplay/AlertsCounter";
import { AlertsModal } from "shared/components/feedback/InfoMessage/AlertsModal";
import { IconButton } from "shared/components/Spor";
import { useOnOutsideMouseclick } from "shared/hooks/useOnOutsideMouseclick";
import { useTheme } from "styled-components";
import { useWebsocket } from "websocket/useWebsocket";
import {
  BackendIdentifier,
  TrainInformationMessage,
  TrainResumedMessage,
  WebsocketTopic,
} from "websocket/websocketSharedWorkerTypes";

export const OpenAlerts = () => {
  const theme = useTheme();

  const [showModal, setShowModal] = useState(false);

  // Consider useStateTrainsResumedMock.tsx if you want trainsResumed(mock) without using websockets
  const [trainsResumed, setTrainsResumed] = useState<TrainResumed[]>([]);
  const hasAlerts = trainsResumed.length > 0;

  const TRAIN_RESUMED_QUERY_KEY = "trainResumed";
  const { data, isSuccess } = useQuery({
    queryKey: [TRAIN_RESUMED_QUERY_KEY],
    retry: 1,
    queryFn: ({ signal }) =>
      queryFnGET<TrainResumedResponse>({
        signal,
        url: `${getBackendUrl()}/train-resumed`,
      }),
  });
  useEffect(() => {
    if (!isSuccess) return;
    setTrainsResumed(data.trainsResumed);
  }, [isSuccess]);

  const handleTrainResumed = useCallback(
    ({ message }: TrainResumedMessage) => {
      handleTrainResumedResponse(message, trainsResumed, setTrainsResumed);
    },
    [trainsResumed, setTrainsResumed],
  );

  const handleTrainInformation = useCallback(
    ({ message }: TrainInformationMessage) => {
      const matchingVersion = message.versions.find(
        (version) =>
          version.trainIdentifier.nominalDate &&
          trainsResumed.find(
            (train) =>
              train.trainIdentifier.identifier ===
              version.trainIdentifier.identifier,
          ),
      );

      if (
        matchingVersion?.state === TrainInfoState.CLOSED ||
        matchingVersion?.state === TrainInfoState.REPLACED
      ) {
        const matchingTrainId = matchingVersion.trainIdentifier.identifier;
        setTrainsResumed(
          trainsResumed.filter(
            (it) => it.trainIdentifier.identifier !== matchingTrainId,
          ),
        );
      }
    },
    [trainsResumed, setTrainsResumed],
  );

  useWebsocket(BackendIdentifier.DROPS_BACKEND, {
    [WebsocketTopic.TRAIN_RESUMED]: handleTrainResumed,
    [WebsocketTopic.TRAIN_INFORMATION]: handleTrainInformation,
  });

  const { mouseclickRef } = useOnOutsideMouseclick<HTMLDivElement>(() => {
    setShowModal(false);
  });

  return (
    <div ref={mouseclickRef} style={{ position: "relative" }}>
      {showModal && (
        <AlertsModal
          trainsResumed={trainsResumed}
          setTrainsResumed={setTrainsResumed}
          setShowModal={setShowModal}
        />
      )}
      {hasAlerts && (
        <AlertsCounter
          position="absolute"
          right="9px"
          top="-3px"
          alerts={trainsResumed.length}
          semantics="warning"
          zIndex={1}
        />
      )}
      <IconButton
        mx="12px"
        variant="tertiary"
        aria-label="Train alerts"
        icon={
          hasAlerts ? <NotificationFill30Icon /> : <NotificationOutline30Icon />
        }
        onClick={() => {
          setShowModal(!showModal);
        }}
        color={hasAlerts ? theme.colorTextMain : undefined}
      />
    </div>
  );
};
